import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus, Hammer, CheckCircle2, AlertTriangle, FileText, Eye, Printer, Trash2, Download } from 'lucide-react';
import {
  apiCreatePOrder, apiListPOrders, apiGetPOrder, apiClosePOrder,
  apiUpdatePOrder, apiListProducts, apiListWarehouses, apiDeletePOrder,
  apiOpenProductionOrderPdf, apiDownloadProductionOrderPdf, apiListPartners,
  apiOpenProductionOrderBomPdf, apiDownloadProductionOrderBomPdf
} from '../../erpApi';
import { toast } from 'sonner';

const PRIORITY_LABEL = { low: 'Bassa', normal: 'Normale', high: 'Alta', urgent: 'Urgente' };
const PRIORITY_COLOR = { low: 'bg-slate-100 text-slate-600', normal: 'bg-blue-100 text-blue-700', high: 'bg-amber-100 text-amber-700', urgent: 'bg-red-100 text-red-700' };
const STATUS_LABEL = { open: 'Aperto', preparing: 'In preparazione', in_progress: 'In lavorazione', completed: 'Completato', closed: 'Chiuso', cancelled: 'Annullato' };
const STATUS_COLOR = { open: 'bg-slate-100 text-slate-700', preparing: 'bg-amber-100 text-amber-700', in_progress: 'bg-blue-100 text-blue-700', completed: 'bg-emerald-100 text-emerald-700', closed: 'bg-emerald-200 text-emerald-900', cancelled: 'bg-red-100 text-red-700' };

const OrderForm = ({ products, warehouses, customers, onSubmit, onCancel }) => {
  const [customerId, setCustomerId] = useState('');
  const finished = products.filter(p =>
    (p.kind === 'finished' || p.kind === 'semi_finished') &&
    (!customerId || p.customer_id === customerId)
  );
  const [f, setF] = useState({
    product_id: '', quantity: 1, planned_date: new Date().toISOString().slice(0, 10),
    priority: 'normal', source_warehouse_id: '', target_warehouse_id: '',
    customer_order_number: '', notes: ''
  });
  // Reset product when customer changes
  useEffect(() => {
    if (customerId && f.product_id) {
      const p = products.find(x => x.id === f.product_id);
      if (!p || p.customer_id !== customerId) setF(prev => ({ ...prev, product_id: '' }));
    }
  }, [customerId]); // eslint-disable-line
  const submit = (e) => {
    e.preventDefault();
    if (!customerId) return toast.error('Cliente obbligatorio');
    if (!f.product_id || !f.source_warehouse_id || !f.target_warehouse_id) return toast.error('Compila tutti i campi obbligatori');
    onSubmit({ ...f, quantity: parseFloat(f.quantity), customer_id: customerId });
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="po-form">
      <div className="space-y-1.5"><Label>Cliente *</Label>
        <Select value={customerId} onValueChange={setCustomerId}>
          <SelectTrigger data-testid="po-customer"><SelectValue placeholder="Seleziona cliente..." /></SelectTrigger>
          <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>[{c.code}] {c.business_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5"><Label>Prodotto finito *</Label>
        <Select value={f.product_id} onValueChange={(v) => setF({ ...f, product_id: v })} disabled={!customerId}>
          <SelectTrigger data-testid="po-product"><SelectValue placeholder={customerId ? 'Seleziona...' : 'Prima scegli un cliente'} /></SelectTrigger>
          <SelectContent>
            {finished.length === 0 && customerId && <div className="px-2 py-1.5 text-xs text-slate-500">Nessun prodotto associato al cliente</div>}
            {finished.map(p => <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Quantità da produrre *</Label><Input data-testid="po-qty" type="number" step="0.001" min="0.001" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} required /></div>
        <div className="space-y-1.5"><Label>Data prevista *</Label><Input type="date" value={f.planned_date} onChange={(e) => setF({ ...f, planned_date: e.target.value })} required /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Magazzino prelievo MP *</Label>
          <Select value={f.source_warehouse_id} onValueChange={(v) => setF({ ...f, source_warehouse_id: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Magazzino destinazione PF *</Label>
          <Select value={f.target_warehouse_id} onValueChange={(v) => setF({ ...f, target_warehouse_id: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5"><Label>Priorità</Label>
        <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="low">Bassa</SelectItem><SelectItem value="normal">Normale</SelectItem><SelectItem value="high">Alta</SelectItem><SelectItem value="urgent">Urgente</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5"><Label>Numero ordine cliente</Label><Input data-testid="po-customer-order-num" value={f.customer_order_number} onChange={(e) => setF({ ...f, customer_order_number: e.target.value })} placeholder="es. ORD-2026-456 del cliente" /></div>
      <div className="space-y-1.5"><Label>Note</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
      <DialogFooter><Button type="button" variant="outline" onClick={onCancel}>Annulla</Button><Button data-testid="po-submit" type="submit" className="bg-blue-700 hover:bg-blue-800">Crea ordine</Button></DialogFooter>
    </form>
  );
};

const OrderDetail = ({ order, onClose, onChanged }) => {
  const [detail, setDetail] = useState(order);
  const [closeMode, setCloseMode] = useState(false);
  const [closeForm, setCloseForm] = useState({ produced_quantity: order.quantity, scrap_quantity: 0, lot_number: '', notes: '' });
  useEffect(() => { apiGetPOrder(order.id).then(setDetail); }, [order.id]);
  const close = async () => {
    try {
      await apiClosePOrder(order.id, { ...closeForm, produced_quantity: parseFloat(closeForm.produced_quantity), scrap_quantity: parseFloat(closeForm.scrap_quantity) || 0 });
      toast.success('Ordine chiuso · materie prime scaricate, prodotto finito caricato'); onClose(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore chiusura'); }
  };
  const setStatus = async (status) => {
    await apiUpdatePOrder(order.id, { status });
    toast.success('Stato aggiornato'); apiGetPOrder(order.id).then(setDetail); onChanged?.();
  };
  const hasShortage = (detail.required_materials || []).some(m => m.shortage > 0);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="font-mono text-base bg-blue-50 text-blue-800 px-2 py-1 rounded">{detail.number}</span>
            <span>Ordine di lavorazione</span>
          </DialogTitle>
          <DialogDescription>
            {detail.product_sku} — {detail.product_name} · Q.tà: <strong>{detail.quantity}</strong>
            <span className={`ml-3 inline-block text-[10px] px-2 py-0.5 rounded font-medium ${STATUS_COLOR[detail.status]}`}>{STATUS_LABEL[detail.status]}</span>
          </DialogDescription>
        </DialogHeader>

        {hasShortage && detail.status !== 'closed' && (
          <div className="p-3 bg-red-50 border border-red-200 rounded flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-700 mt-0.5" />
            <div className="text-xs text-red-800">Disponibilità insufficiente per alcuni componenti. La chiusura potrebbe fallire se le giacenze non vengono ripristinate.</div>
          </div>
        )}

        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase text-slate-500">Materiali necessari (prelievo)</div>
          <div className="bg-white border border-slate-200 rounded overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50"><tr>
                <th className="text-left px-3 py-1.5 text-[10px] font-medium uppercase text-slate-600">SKU</th>
                <th className="text-left px-3 py-1.5 text-[10px] font-medium uppercase text-slate-600">Componente</th>
                <th className="text-left px-3 py-1.5 text-[10px] font-medium uppercase text-slate-600">Ubicazione</th>
                <th className="text-right px-3 py-1.5 text-[10px] font-medium uppercase text-slate-600">Richiesto</th>
                <th className="text-right px-3 py-1.5 text-[10px] font-medium uppercase text-slate-600">Disponibile</th>
                <th className="text-right px-3 py-1.5 text-[10px] font-medium uppercase text-slate-600">Mancanza</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {(detail.required_materials || []).map((m) => (
                  <tr key={m.product_id} className={m.shortage > 0 ? 'bg-red-50/30' : ''}>
                    <td className="px-3 py-1.5 font-mono text-xs">{m.product_sku}</td>
                    <td className="px-3 py-1.5">{m.product_name}</td>
                    <td className="px-3 py-1.5 text-xs font-mono">{m.location_code || '—'}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-medium">{m.required_quantity} {m.unit_of_measure}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{m.available}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${m.shortage > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{m.shortage > 0 ? m.shortage : '✓'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {detail.status === 'closed' ? (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-sm">
            <div className="font-semibold text-emerald-900 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />Ordine chiuso</div>
            <div className="text-xs text-emerald-800 mt-1">Prodotti: <strong>{detail.produced_quantity}</strong>{detail.scrap_quantity > 0 && <> · Scarti: <strong>{detail.scrap_quantity}</strong></>} · Lotto: <strong>{detail.lot_number || '—'}</strong></div>
          </div>
        ) : closeMode ? (
          <div className="p-3 bg-slate-50 border border-slate-200 rounded space-y-2">
            <div className="text-sm font-semibold">Chiusura ordine</div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1"><Label className="text-xs">Q.tà prodotta *</Label><Input type="number" step="0.001" value={closeForm.produced_quantity} onChange={(e) => setCloseForm({ ...closeForm, produced_quantity: e.target.value })} className="h-8" /></div>
              <div className="space-y-1"><Label className="text-xs">Scarti</Label><Input type="number" step="0.001" value={closeForm.scrap_quantity} onChange={(e) => setCloseForm({ ...closeForm, scrap_quantity: e.target.value })} className="h-8" /></div>
              <div className="space-y-1"><Label className="text-xs">Lotto</Label><Input value={closeForm.lot_number} onChange={(e) => setCloseForm({ ...closeForm, lot_number: e.target.value })} className="h-8 font-mono" placeholder="es. L20260301" /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Note chiusura</Label><Input value={closeForm.notes} onChange={(e) => setCloseForm({ ...closeForm, notes: e.target.value })} className="h-8" /></div>
            <div className="flex gap-2 justify-end pt-1">
              <Button size="sm" variant="outline" onClick={() => setCloseMode(false)}>Annulla</Button>
              <Button data-testid="po-close-confirm" size="sm" className="bg-emerald-700 hover:bg-emerald-800" onClick={close}>Conferma chiusura</Button>
            </div>
          </div>
        ) : (
          <DialogFooter className="gap-2">
            <Button
              size="sm" variant="outline"
              data-testid="po-bom-open"
              onClick={() => apiOpenProductionOrderBomPdf(detail.id).catch(() => toast.error('Errore generazione distinta'))}
              title="Visualizza distinta base"
              className="gap-1.5"
            >
              <FileText className="w-3.5 h-3.5" />Distinta Base
            </Button>
            <Button
              size="sm" variant="outline"
              data-testid="po-bom-download"
              onClick={() => apiDownloadProductionOrderBomPdf(detail.id, `DB-${detail.number}.pdf`).catch(() => toast.error('Errore scarico distinta'))}
              title="Scarica distinta base"
              className="gap-1.5"
            >
              <Printer className="w-3.5 h-3.5" />Stampa DB
            </Button>
            {detail.status === 'open' && <Button size="sm" variant="outline" onClick={() => setStatus('preparing')}>→ In preparazione</Button>}
            {detail.status === 'preparing' && <Button size="sm" variant="outline" onClick={() => setStatus('in_progress')}>→ In lavorazione</Button>}
            {['in_progress', 'preparing', 'open'].includes(detail.status) && <Button data-testid="po-close-btn" size="sm" className="bg-emerald-700 hover:bg-emerald-800 gap-1.5" onClick={() => setCloseMode(true)}><CheckCircle2 className="w-3.5 h-3.5" />Chiudi ordine</Button>}
            <Button size="sm" variant="outline" onClick={onClose}>Chiudi finestra</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ProductionOrdersManager = () => {
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const reload = async () => {
    const params = {};
    if (statusFilter !== 'all') params.status = statusFilter;
    if (customerFilter !== 'all') params.customer_id = customerFilter;
    const [o, p, w, c] = await Promise.all([
      apiListPOrders(params), apiListProducts(), apiListWarehouses(), apiListPartners({ kind: 'customer' })
    ]);
    setList(o); setProducts(p); setWarehouses(w); setCustomers(c);
  };
  useEffect(() => { reload(); }, [statusFilter, customerFilter]); // eslint-disable-line
  const handleSubmit = async (data) => {
    try {
      await apiCreatePOrder(data); toast.success('Ordine creato'); setDialogOpen(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  const openPdf = async (o) => {
    try { await apiOpenProductionOrderPdf(o.id); }
    catch { toast.error('Errore apertura PDF'); }
  };
  return (
    <div className="space-y-3" data-testid="po-manager">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="po-status-filter" className="h-9 w-[180px] text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger data-testid="po-customer-filter" className="h-9 w-[200px] text-sm"><SelectValue placeholder="Cliente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i clienti</SelectItem>
              {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.business_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild><Button data-testid="po-new" className="bg-blue-700 hover:bg-blue-800 gap-2 h-9"><Plus className="w-4 h-4" />Nuovo ordine lavorazione</Button></DialogTrigger>
          <DialogContent className="sm:max-w-xl"><DialogHeader><DialogTitle>Nuovo ordine di lavorazione</DialogTitle><DialogDescription>Seleziona prima il cliente, poi i suoi prodotti.</DialogDescription></DialogHeader>
            <OrderForm products={products} warehouses={warehouses} customers={customers} onSubmit={handleSubmit} onCancel={() => setDialogOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>
      {list.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300"><Hammer className="w-10 h-10 text-slate-300 mx-auto mb-2" /><div className="text-sm">Nessun ordine</div></div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Numero</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Cliente</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Prodotto</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Q.tà</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Data</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Ord. cliente</th>
                <th className="text-center px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Priorità</th>
                <th className="text-center px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Stato</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map(o => (
                <tr key={o.id} className="hover:bg-slate-50" data-testid={`po-row-${o.number}`}>
                  <td className="px-3 py-2 font-mono font-semibold text-blue-700 cursor-pointer" onClick={() => setDetail(o)}>{o.number}</td>
                  <td className="px-3 py-2 text-xs text-slate-700">{o.customer_name || '—'}</td>
                  <td className="px-3 py-2 cursor-pointer" onClick={() => setDetail(o)}><span className="font-mono text-xs text-slate-500 mr-1">{o.product_sku}</span>{o.product_name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{o.quantity}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{o.planned_date}</td>
                  <td className="px-3 py-2 text-xs text-slate-700 font-mono">{o.customer_order_number || '—'}</td>
                  <td className="px-3 py-2 text-center"><span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${PRIORITY_COLOR[o.priority]}`}>{PRIORITY_LABEL[o.priority]}</span></td>
                  <td className="px-3 py-2 text-center"><span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${STATUS_COLOR[o.status]}`}>{STATUS_LABEL[o.status]}</span></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Dettaglio" onClick={() => setDetail(o)}><Eye className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Apri PDF Ordine" data-testid={`po-open-${o.number}`} onClick={() => openPdf(o)}><Printer className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Scarica PDF Ordine" data-testid={`po-download-${o.number}`} onClick={() => apiDownloadProductionOrderPdf(o.id, `${o.number}.pdf`)}><Download className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-purple-700" title="Stampa Distinta Base" data-testid={`po-bom-${o.number}`} onClick={() => apiOpenProductionOrderBomPdf(o.id).catch(() => toast.error('Errore distinta'))}><FileText className="w-3.5 h-3.5" /></Button>
                    <Button
                      size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                      title={o.status === 'closed' ? 'Elimina ordine chiuso (richiede conferma)' : 'Elimina ordine'}
                      data-testid={`po-delete-${o.number}`}
                      onClick={async () => {
                        const isClosed = o.status === 'closed';
                        const msg = isClosed
                          ? `⚠️ ATTENZIONE: l'ordine ${o.number} è CHIUSO.\n\nLa cancellazione NON ripristina le giacenze movimentate.\n\nConfermi l'eliminazione?`
                          : `Eliminare l'ordine ${o.number}?`;
                        if (!window.confirm(msg)) return;
                        try {
                          await apiDeletePOrder(o.id, isClosed);
                          toast.success('Ordine eliminato');
                          reload();
                        } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
                      }}
                    ><Trash2 className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {detail && <OrderDetail order={detail} onClose={() => setDetail(null)} onChanged={reload} />}
    </div>
  );
};

export default ProductionOrdersManager;
